import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Grid,
  Chip,
  alpha,
  useTheme,
  Icon,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import moment from "moment";
import { JournalEntryDialogProps } from "../types";
import {
  formatCurrency,
  getSourceModuleColor,
  calculateJournalTotals,
} from "../../../utils";

export const JournalEntryDialog: React.FC<JournalEntryDialogProps> = ({
  open,
  onClose,
  journalEntry,
}) => {
  const theme = useTheme();

  if (!journalEntry) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Icon>receipt_long</Icon>
          <Box>
            <Typography variant="h6">Journal Entry Details</Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {journalEntry._id}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          {/* Journal Header */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6}>
              <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>
                Date
              </Typography>
              <Typography variant="body1">
                {moment(journalEntry.entry_date).format("DD MMMM YYYY")}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>
                Posted By
              </Typography>
              <Typography variant="body1">
                {(journalEntry as any).posted_by || "System"}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>
                Source Module
              </Typography>
              <Chip
                size="small"
                label={journalEntry.source_module}
                sx={{
                  bgcolor: alpha(
                    getSourceModuleColor(journalEntry.source_module),
                    0.1
                  ),
                  color: getSourceModuleColor(journalEntry.source_module),
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>
                Status
              </Typography>
              <Chip
                size="small"
                label={journalEntry.status}
                color={
                  journalEntry.status === "posted"
                    ? "success"
                    : journalEntry.status === "draft"
                    ? "warning"
                    : "error"
                }
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>
                Narration
              </Typography>
              <Typography variant="body1">{journalEntry.narration}</Typography>
            </Grid>
          </Grid>

          {/* Journal Lines */}
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
            Journal Lines
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Account</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    Debit
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    Credit
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {journalEntry.journal_lines.map((line, index) => (
                  <TableRow key={line.line_id || `line-${index}`}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {line.account_code} - {line.account_name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {line.description || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: "monospace",
                          fontWeight: line.debit ? 600 : 400,
                          color: line.debit ? "success.main" : "text.disabled",
                        }}
                      >
                        {line.debit ? formatCurrency(line.debit) : "—"}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: "monospace",
                          fontWeight: line.credit ? 600 : 400,
                          color: line.credit ? "error.main" : "text.disabled",
                        }}
                      >
                        {line.credit ? formatCurrency(line.credit) : "—"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
                {/* Totals Row */}
                <TableRow
                  sx={{ bgcolor: alpha(theme.palette.primary.main, 0.02) }}
                >
                  <TableCell colSpan={2} sx={{ fontWeight: 700 }}>
                    TOTALS
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontWeight: 700, fontFamily: "monospace" }}
                  >
                    {formatCurrency(
                      calculateJournalTotals(journalEntry).totalDebits
                    )}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontWeight: 700, fontFamily: "monospace" }}
                  >
                    {formatCurrency(
                      calculateJournalTotals(journalEntry).totalCredits
                    )}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          {/* Tags */}
          {(journalEntry as any).journal_tags &&
            (journalEntry as any).journal_tags.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 1, color: "text.secondary" }}
                >
                  Tags
                </Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  {(journalEntry as any).journal_tags.map(
                    (tag: any, index: number) => (
                      <Chip
                        key={tag.id || `tag-${index}`}
                        size="small"
                        label={`${tag.tag_type}: ${tag.tag_value}`}
                        variant="outlined"
                      />
                    )
                  )}
                </Box>
              </Box>
            )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" startIcon={<Icon>edit</Icon>}>
          Edit Journal
        </Button>
      </DialogActions>
    </Dialog>
  );
};
